import { Filters, SearchMetadata } from "@nerdjs/nerd-core";
import {
  DeleteAction,
  GetAction,
  PostAction,
  PutAction,
} from "@nerdjs/nerd-network";
import { AnyAction, createAction, Dispatch, nanoid } from "@reduxjs/toolkit";
import { Timer, Timer_Entity } from "../../entities/timer";

export const setStagedTimer = createAction(
  "setStagedTimer",
  function prepare(stagedTimer?: Timer_Entity) {
    return {
      payload: {
        stagedTimer,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const clearCurrentTimer = createAction(
  "clearCurrentTimer",
  function prepare() {
    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTimerFormOpen = createAction(
  "setTimerFormOpen",
  function prepare(open: boolean) {
    return {
      payload: {
        open,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTimerLoading = createAction(
  "setTimerLoading",
  function prepare(loading: boolean) {
    return {
      payload: {
        loading,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTimerSearchOpen = createAction(
  "setTimerSearchOpen",
  function prepare(searchOpen: boolean) {
    return {
      payload: {
        searchOpen,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setTimerFilters = createAction(
  "setTimerFilters",
  function prepare(filters?: Filters) {
    return {
      payload: {
        filters,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateStagedTimer = createAction(
  "updateStagedTimer",
  function prepare(stagedTimer?: Partial<Timer_Entity>) {
    return {
      payload: {
        stagedTimer,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const setSelectedTimerID = createAction(
  "setSelectedTimerID",
  function prepare(timerID?: number) {
    return {
      payload: {
        timerID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTimers = createAction(
  "kronos/timer/getTimers",
  function prepare() {
    const request = new GetAction(`/kronos/timers`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const timers = Timer.fromList(json);
      dispatch(getTimersSuccess(timers));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTimersSuccess = createAction(
  "kronos/timer/getTimerSuccess",
  function prepare(timers: Timer[]) {
    return {
      payload: {
        timers,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTimersByFilter = createAction(
  "kronos/timer/getTimersByFilter",
  function prepare(query: { [key: string]: unknown }) {
    const request = new GetAction(`/kronos/timers`, query);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const timers = Timer.fromList(json);
      dispatch(getTimersSuccess(timers));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTimersByFilterSuccess = createAction(
  "kronos/timer/getTimersByFilterSuccess",
  function prepare(timers: Timer[]) {
    return {
      payload: {
        timers,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getCurrentTimer = createAction(
  "kronos/timer/getCurrentTimer",
  function prepare() {
    const request = new GetAction(`/kronos/timers/current`);

    request.decodeResponse = (dispatch: Dispatch, json: Timer) => {
      const timer = new Timer(json);
      dispatch(getCurrentTimerSuccess(timer));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getCurrentTimerSuccess = createAction(
  "kronos/timer/getCurrentTimerSuccess",
  function prepare(timer?: Timer) {
    return {
      payload: {
        timer,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTimerById = createAction(
  "kronos/timer/getById",
  function prepare(id: number) {
    const request = new GetAction(`/kronos/timers/${id}`);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const timer = new Timer(json);
      dispatch(getTimerByIdSuccess(timer));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTimerByIdSuccess = createAction(
  "kronos/timer/getTimerByIdSuccess",
  function prepare(timer?: Timer) {
    return {
      payload: {
        timer,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const deleteTimer = createAction(
  "kronos/removeTimerById",
  function prepare(id: number, successAction?: AnyAction) {
    const request = new DeleteAction(`/kronos/timers/${id}`);

    if (successAction)
      request.decodeResponse = (dispatch: Dispatch) => {
        dispatch(deleteTimerSuccess(id));
      };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const deleteTimerSuccess = createAction(
  "kronos/timers/deleteTimerSuccess",
  function prepare(timerID: number) {
    return {
      payload: {
        timerID,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const updateTimer = createAction(
  "kronos/timers/updateTimer",
  function prepare(id: number, body: Partial<Timer>) {
    const request = new PutAction(`/kronos/timers/${id}`, body);

    request.decodeResponse = (dispatch: Dispatch) => {
      dispatch(updateTimerSuccess(true));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const updateTimerSuccess = createAction(
  "kronos/timers/updateTimerSuccess",
  function prepare(success: boolean) {
    return {
      payload: {
        success,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const createTimer = createAction(
  "kronos/timers/createTimer",
  function prepare(body: Partial<Timer>) {
    const request = new PostAction(`/kronos/timers`, body);

    request.decodeResponse = (dispatch: Dispatch, json: unknown) => {
      const timer = new Timer(json);
      dispatch(createTimerSuccess(timer));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const createTimerSuccess = createAction(
  "kronos/timers/createTimerSuccess",
  function prepare(timer: Timer) {
    return {
      payload: {
        timer,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);

export const getTimersSearchMetadata = createAction(
  "kronos/getTimersSearchMetadata",
  function prepare() {
    const request = new GetAction(`/kronos/timers/searchMetadata`);

    request.decodeResponse = (dispatch: Dispatch, json: SearchMetadata) => {
      dispatch(getTimersSearchMetadataSuccess(json));
    };

    return {
      payload: {
        id: nanoid(),
        createdAt: new Date().toISOString(),
        request,
      },
    };
  }
);

export const getTimersSearchMetadataSuccess = createAction(
  "kronos/getTimersSearchMetadataSuccess",
  function prepare(timersSearchMetadata: SearchMetadata) {
    return {
      payload: {
        timersSearchMetadata,
        id: nanoid(),
        createdAt: new Date().toISOString(),
      },
    };
  }
);
